import { Injectable } from "@angular/core";
import { ANSWERS, FunctionalGroup } from "./answers.data";

import { pick, round } from "lodash-es";

import { AlertService } from "../alerts/alerts.service";
import { CompoundTree } from "../../shared/compound/compound-tree";
import { CompoundUtils } from "../../shared/compound/compound-utils";

import { console } from "#/log";
import { Compounds } from "#/declarations";

/**
 * A Service responsible for checking answers.
 */
@Injectable()
export class AnswerService {
  constructor(public alertService: AlertService) {}

  /**
   * Returns a flat Structure of the solution molecule for comparison.
   * Returns an empty structure if no solution is found.
   */
  public getSolutionStructure(sampleId: string): Compounds.Structure {
    if (
      ANSWERS.hasOwnProperty(sampleId) &&
      ANSWERS[sampleId].hasOwnProperty("build")
    ) {
      const build = ANSWERS[sampleId].build;

      // Ensure build is not undefined
      if (build !== undefined) {
        return CompoundTree.getStructureFromTree(build);
      }
    }
    return { atoms: [], bonds: [] };
  }

  getIncorrectBuildParameters(
    sampleId: string,
    build: Compounds.Structure,
  ): Array<string> {
    let answer = this.getSolutionStructure(sampleId);
    return this.validateBuild(build, answer);
  }

  validateBuild(build: Compounds.Structure, answer: Compounds.Structure) {
    let incorrect = [];
    let buildMass = round(CompoundUtils.compoundMass(build), 2);
    let answerMass = round(CompoundUtils.compoundMass(answer), 2);
    let sameComposition =
      CompoundUtils.haveSameAtoms(build, answer) &&
      CompoundUtils.haveSameBonds(build, answer);
    let areIdentical = CompoundUtils.areIdentical(build, answer);
    if (buildMass !== answerMass) {
      incorrect.push("mass");
    }
    if (!sameComposition) {
      incorrect.push("composition");
    }
    if (sameComposition && !areIdentical) {
      incorrect.push("isomer");
    }
    return incorrect;
  }

  /**
   * Checks if a given range assignment is correct for a given sample.
   * Returns incorrect fields in an array. Returns null if type is invalid.
   */
  public getIncorrectFields(sampleId: string, assignment: any) {
    // public getIncorrectFields(sampleId: string, assignment: { $key: string; }) {
    let answerValue = this.getAnswer(sampleId, assignment.$key);
    console.debug("answers-service: Answer is ", answerValue);
    let checkValue = pick(
      assignment,
      Object.keys(answerValue),
    ) as Partial<FunctionalGroup>;
    let fields = [];

    if (answerValue.hasOwnProperty("assigned")) {
      if (
        !checkValue.assigned ||
        checkValue.assigned.name !== answerValue.assigned.name
      ) {
        fields.push("assigned");
      }
    }

    if (answerValue.hasOwnProperty("splitting")) {
      // Check if both values are defined before parsing
      const splittingCheckValue =
        checkValue.splitting !== undefined
          ? parseInt(String(checkValue.splitting))
          : null;
      const splittingAnswerValue =
        answerValue.splitting !== undefined
          ? parseInt(answerValue.splitting.toString())
          : null;

      if (splittingCheckValue !== splittingAnswerValue) {
        fields.push("splitting");
      }
    }

    if (answerValue.hasOwnProperty("hydrogens")) {
      // Check if both values are defined before parsing
      const hydrogensCheckValue =
        checkValue.hydrogens !== undefined
          ? parseInt(String(checkValue.hydrogens))
          : null;
      const hydrogensAnswerValue =
        answerValue.hydrogens !== undefined
          ? parseInt(answerValue.hydrogens.toString())
          : null;

      if (hydrogensCheckValue !== hydrogensAnswerValue) {
        fields.push("hydrogens");
      }
    }

    return fields;
  }

  /**
   * Get the answer for the range in the sample
   */
  public getAnswer(sampleId: string, rangeId: string): FunctionalGroup {
    if (
      ANSWERS.hasOwnProperty(sampleId) &&
      ANSWERS[sampleId].ranges.hasOwnProperty(rangeId)
    ) {
      return ANSWERS[sampleId].ranges[rangeId];
    }
    throw Error("Could not find an answer for the range " + rangeId);
  }
}
